import { useEffect, useState } from 'react';
import { GoogleButton } from '../components/GoogleButton.tsx';
import { Button, Card } from '../components/ui.tsx';
import { AUTH_COPY, type AuthError } from '../lib/auth.ts';
import { useRepo } from '../lib/hooks.ts';

/** J1 steps 1–3 and the gate states (ADR-0016). */

/** Set by wipeDevice (J8): the confirmation must survive the sign-out and the reload that follow it. */
export const WIPED_KEY = 'repwise-wiped';

function Shell({ children }: { children: React.ReactNode }) {
  const [wiped] = useState(() => {
    try {
      return sessionStorage.getItem(WIPED_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!wiped) return;
    try {
      sessionStorage.removeItem(WIPED_KEY);
    } catch {
      /* storage unavailable */
    }
  }, [wiped]);
  return (
    <main className="min-h-full p-5 pb-16 max-w-[480px] mx-auto flex flex-col">
      {wiped && (
        <p role="status" className="bg-surface-raised rounded-[14px] p-3 text-sm">
          Repwise data has been removed from this device.
        </p>
      )}
      {children}
    </main>
  );
}

function Brand() {
  return <img src="/icons/lockup.svg" alt="Repwise" width={224} height={50} className="mt-10 mb-8 w-56 h-auto" />;
}

export function Welcome({ onStart }: { onStart: () => void }) {
  return (
    <Shell>
      <Brand />
      <h1 className="text-title num">StrongLifts 5×5, without the busywork.</h1>
      <p className="text-text-muted mt-3">Your next workout, weights and plates are always ready. Works offline in the gym. Your training is backed up to your account.</p>
      <div className="mt-auto pt-10">
        <Button onClick={onStart}>Get started</Button>
        <p className="text-sm text-text-muted mt-3 text-center">Already use Repwise? Sign in with the same email and your workouts come back.</p>
      </div>
    </Shell>
  );
}

const COOLDOWN_S = 60;

export function SignIn() {
  const repo = useRepo();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  if (!repo.auth) return null;
  const auth = repo.auth;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const send = async () => {
    setBusy(true);
    setError(null);
    const r = await auth.sendCode(email.trim());
    setBusy(false);
    if (!r.ok) return setError(r.error);
    setStage('code');
    setCooldown(COOLDOWN_S);
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    const r = await auth.verifyCode(email.trim(), code);
    setBusy(false);
    if (!r.ok) return setError(r.error);
    await repo.signedIn(r.user);
  };

  return (
    <Shell>
      <Brand />
      {stage === 'email' ? (
        <>
          <h1 className="text-title num">Sign in or create your account</h1>
          <p className="text-text-muted mt-2">We'll email you a code. No password to remember.</p>
          <GoogleButton />
          <label className="block mt-6 text-sm" htmlFor="email">
            Email
          </label>
          <input id="email" className="w-full min-h-12 mt-1 rounded-[14px] bg-surface-sunken border border-outline px-3" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} />
          {error && <p className="text-miss text-sm mt-3" role="alert">{AUTH_COPY[error]}</p>}
          <div className="mt-6">
            <Button onClick={() => void send()} disabled={!validEmail || busy}>
              {busy ? 'Sending…' : 'Email me a code'}
            </Button>
          </div>
        </>
      ) : (
        <>
          <h1 className="text-title num">Check your email</h1>
          <p className="text-text-muted mt-2">
            We sent a code to <span className="text-text">{email.trim()}</span>. It works once, for 10 minutes. Not there? Check your spam folder.
          </p>
          <label className="block mt-6 text-sm" htmlFor="code">
            Code
          </label>
          {/* Length-agnostic: the project's OTP length is a Supabase setting (6–10 digits). */}
          <input id="code" className="w-full min-h-12 mt-1 rounded-[14px] bg-surface-sunken border border-outline px-3 num text-num-l tracking-widest" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={10} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))} />
          {error && <p className="text-miss text-sm mt-3" role="alert">{AUTH_COPY[error]}</p>}
          <div className="mt-6 space-y-3">
            <Button onClick={() => void verify()} disabled={code.length < 6 || busy}>
              {busy ? 'Checking…' : 'Sign in'}
            </Button>
            <div className="flex justify-between">
              <Button kind="text" onClick={() => (setStage('email'), setCode(''), setError(null))}>
                Change email
              </Button>
              <Button kind="text" onClick={() => void send()} disabled={cooldown > 0 || busy}>
                {cooldown > 0 ? `Resend in ${cooldown} s` : 'Resend code'}
              </Button>
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}

export function Claim() {
  const { engine, user } = useRepo();
  const [preview, setPreview] = useState<{ events: number; workouts: number } | null>(null);
  useEffect(() => void engine.claimPreview().then(setPreview), [engine]);
  const email = user()?.email ?? 'your account';
  return (
    <Shell>
      <Brand />
      <h1 className="text-title num">Workouts on this device</h1>
      <p className="text-text-muted mt-2">
        This device has {preview ? `${preview.workouts} workout${preview.workouts === 1 ? '' : 's'}` : 'some workouts'} that aren't in an account yet. Attach them to {email}?
      </p>
      <div className="mt-auto pt-10 space-y-3">
        <Button onClick={() => void engine.claim().then(() => engine.drain())}>Attach and sync</Button>
        <Button kind="secondary" onClick={() => void engine.useAccountData().then(() => engine.drain())}>
          Use my account's data instead
        </Button>
        <p className="text-sm text-text-muted">If you use your account's data, this device's workouts are kept aside, not deleted.</p>
      </div>
    </Shell>
  );
}

export function Restoring({ failed, onRetry }: { failed: boolean; onRetry: () => void }) {
  return (
    <Shell>
      <Brand />
      {failed ? (
        <Card>
          <h1 className="text-headline">Couldn't reach your workouts</h1>
          <p className="text-text-muted mt-2">Connect to the internet to restore your account. This only happens once on a new device.</p>
          <div className="mt-4">
            <Button kind="secondary" onClick={onRetry}>
              Try again
            </Button>
          </div>
        </Card>
      ) : (
        <p className="text-text-muted" role="status" aria-live="polite">
          Looking for your workouts…
        </p>
      )}
    </Shell>
  );
}
